import React, { useEffect, useRef, useState } from 'react';
import ArrowRightIcon from '../ArrowRightIcon';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUpcomingRenewals } from '../../../services/subscriptionService';
// Loader removed from this widget per request

const UpcomingRenewals = ({ selectedFrequency }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [renewals, setRenewals] = useState([]);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const fetchRenewals = async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        // Respect the dashboard frequency selection if available
        let timeframe = 'month';
        const tfInput = (selectedFrequency || (typeof window !== 'undefined' && window.__ADMIN_SELECTED_FREQUENCY__)) || 'Monthly';
        const tf = String(tfInput || '').toLowerCase();
        const map = { today: 'today', weekly: 'week', monthly: 'month', yearly: 'year' };
        timeframe = map[tf] || tf || 'month';
        const res = await dispatch(getUpcomingRenewals({ timeframe }))?.unwrap();
        if (!res?.success) {
          setRenewals([]);
          return;
        }

        // Backend may return grouped arrays keyed by timeframe
        const root = res?.data || {};
        const data = root?.data && typeof root?.data === 'object' ? root.data : root;

        // Support both "month/monthly", "week/weekly", etc.
        const tfKeyMap = { today: 'today', week: 'weekly', month: 'monthly', year: 'yearly' };
        const tfAltKeyMap = { today: 'today', week: 'week', month: 'month', year: 'year' };
        const tfKey = tfKeyMap[timeframe] || 'monthly';
        const tfAltKey = tfAltKeyMap[timeframe] || 'month';

        const candidates = [
          tfKey,
          tfAltKey,
          timeframe, // e.g. "month"
          // Common fallbacks
          'upcomingRenewals',
          'renewals',
          'items',
        ];

        let grouped = undefined;
        for (const k of candidates) {
          if (Array.isArray(data?.[k])) {
            grouped = data[k];
            break;
          }
        }

        // Fallback to flat structures
        let list = [];
        if (Array.isArray(grouped)) {
          list = grouped;
        } else if (Array.isArray(data)) {
          list = data;
        } else if (Array.isArray(data?.items)) {
          list = data.items;
        }

        // Important: backend may group by subscription interval (e.g. interval="year")
        // while still being requested with timeframe="month".
        // If the timeframe bucket is empty but other buckets have data, merge them.
        if (!list.length && data && typeof data === "object") {
          const mergedBuckets = Object.values(data).filter(Array.isArray).flat();
          if (mergedBuckets.length) {
            list = mergedBuckets;
          }
        }

        setRenewals(
          (Array.isArray(list) ? list : []).map((item) => ({
            username: item?.user?.name || item?.username || item?.name || '',
            email: item?.user?.email || item?.email || '',
            fundraiser:
              item?.fundraiser?.organizationName ||
              item?.fundraiser?.businessName ||
              item?.fundraiser?.user?.name ||
              item?.fundraiser?.name ||
              item?.fundraiser ||
              '',
            subscriptionId: item?.subscription || item?.subscriptionId || item?.subscription?.id || item?.stripeSubscriptionId || '',
            renewalDate: item?.renewal_date ? new Date(item.renewal_date).toLocaleDateString() : '',
            renewalValue: typeof item?.amount === 'number' ? `$${item.amount.toFixed(2)}` : '',
            status: item?.status || 'active',
            autoRenewal: item?.auto_renewal !== false ? 'Yes' : 'No',
          }))
        );
      } catch (_) {}
      inFlightRef.current = false;
    };
    if (!token) return;
    fetchRenewals();
  }, [dispatch, selectedFrequency, token]);

  const getFrequencyText = (freq) => {
    const f = String(freq || '').toLowerCase();
    if (freq === 'Today') return 'due today';
    if (f === 'weekly' || f === 'week') return 'due this week';
    if (f === 'yearly' || f === 'year') return 'due this year';
    return 'due in the next 30 days';
  };

  return (
    <div className="summary w-full p-4 h-[470px] flex flex-col bg-white rounded-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">Upcoming Renewals (30 Days)</h2>
          <span className="text-[12px] font-poppins text-[#767676] font-[500]">
            {renewals.length} Renewals {getFrequencyText(selectedFrequency)}
          </span>
        </div>
        <button onClick={() => navigate('/admin/user-list?upcomingRenewals=30d')} className="text-[#00ADEF] text-[16px] font-poppins font-[500] border border-[#00ADEF] rounded-[6px] px-4 py-2 transition hover:bg-[#E6F7FB] flex items-center">
          View All
          <ArrowRightIcon className="ml-2" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto pr-1">
        {renewals.length > 0 ? (
          <table className="w-full min-w-[1050px] table-fixed border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-gray-200">
                <th className="w-[12%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Username</th>
                <th className="w-[18%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Email</th>
                <th className="w-[15%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Fundraiser</th>
                <th className="w-[15%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Subscription</th>
                <th className="w-[12%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Renewal Date</th>
                <th className="w-[12%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Renewal Value</th>
                <th className="w-[8%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Status</th>
                <th className="w-[8%] text-left py-3 px-2 text-[13px] font-poppins font-[500] text-[#303030] whitespace-nowrap bg-white">Auto Renewal</th>
              </tr>
            </thead>
            <tbody>
              {renewals.map((renewal, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={renewal.username}>
                    {renewal.username}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={renewal.email}>
                    {renewal.email}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={renewal.fundraiser}>
                    {renewal.fundraiser}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] truncate align-middle" title={renewal.subscriptionId}>
                    {renewal.subscriptionId}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] whitespace-nowrap align-middle">
                    {renewal.renewalDate}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] whitespace-nowrap align-middle font-[500] text-gray-900">
                    {renewal.renewalValue}
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins align-middle">
                    <span className={`px-2 py-0.5 rounded-[4px] text-[11px] font-[500] uppercase ${
                      renewal.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {renewal.status}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-[13px] font-poppins text-[#767676] align-middle">
                    {renewal.autoRenewal}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#767676] text-[14px] py-12">No data</div>
        )}
      </div>
    </div>
  );
};

export default UpcomingRenewals; 
